/** The admin's fleet in plain words: who is online, whose agent is behind, what is failing. Pure; `now` is injectable. */
import { ago } from './format'
import type { CloudInit, FailingCheck, Fleet, FleetHealth, FleetRow } from './schemas'

export type Presence = 'online' | 'offline' | 'unknown'

/**
 * The server's clock as of `now`, from the `at` it stamped on the response and when we received it. The table ticks
 * between polls on this, so a phone whose clock is off still reads the right "last seen".
 */
export function serverNow(fleetAt: string, receivedAt: number, now = Date.now()): number {
  const at = Date.parse(fleetAt)
  return Number.isFinite(at) ? at + (now - receivedAt) : now
}

/** Online until the last heartbeat is older than the API's window, recomputed as the clock ticks between polls. */
export function presence(row: Pick<FleetRow, 'lastHeartbeatAt'>, onlineWithinSec: number, now: number): Presence {
  if (!row.lastHeartbeatAt) return 'unknown'
  return now - Date.parse(row.lastHeartbeatAt) <= onlineWithinSec * 1000 ? 'online' : 'offline'
}

/** "7m", "2h 05m", "3d": how long a box has been quiet. */
export function quietFor(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds))
  if (s < 60) return `${s}s`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ${String(m % 60).padStart(2, '0')}m`
  return `${Math.floor(h / 24)}d`
}

/** The presence chip's words: "last seen 12s ago", "offline for 7m", "no report yet". */
export function seenWords(row: Pick<FleetRow, 'lastHeartbeatAt'>, onlineWithinSec: number, now: number): { presence: Presence; words: string } {
  const p = presence(row, onlineWithinSec, now)
  if (p === 'unknown') return { presence: p, words: 'no report yet' }
  if (p === 'online') return { presence: p, words: `last seen ${ago(row.lastHeartbeatAt, now)}` }
  return { presence: p, words: `offline for ${quietFor((now - Date.parse(row.lastHeartbeatAt!)) / 1000)}` }
}

export interface VersionView {
  /** The version, or plain words when there is none. */
  words: string
  behind: boolean
  /** "behind 0.2.0", "latest", or null when we can't say. */
  note: string | null
}

export function versionView(row: Pick<FleetRow, 'agentVersion' | 'outdated'>, latest: string | null): VersionView {
  if (!row.agentVersion) return { words: 'no agent yet', behind: false, note: null }
  if (row.outdated) return { words: row.agentVersion, behind: true, note: latest ? `behind ${latest}` : 'behind' }
  return { words: row.agentVersion, behind: false, note: latest && row.agentVersion.replace(/^v/, '') === latest.replace(/^v/, '') ? 'latest' : null }
}

/** What a failing check means, written for the admin rather than the founder's "Your user created". */
const CHECK_WORDS: Record<string, string> = {
  machine: 'Machine not created',
  system: 'System not ready',
  address: 'Address not live',
  tools: 'Tools not installed',
  user: "Founder's Linux user missing",
  phone: 'Phone link not ready',
  starter: 'Starter app not ready',
}

export function checkWords(c: FailingCheck): { title: string; detail: string | null } {
  return { title: CHECK_WORDS[c.key] ?? `${c.label}: failing`, detail: c.detail?.trim() || null }
}

/** cloud-init only earns a line when it's still going or went wrong. */
export function cloudInitWords(ci: CloudInit): { words: string; bad: boolean } | null {
  if (!ci) return null
  const s = ci.status.toLowerCase()
  if (s === 'done' && ci.errors.length === 0) return null
  if (s === 'running' || s === 'not started' || s === 'not run') return { words: 'cloud-init still running', bad: false }
  const first = ci.errors[0]
  return { words: `cloud-init ${s === 'done' ? 'finished with errors' : s}${first ? `: ${first}` : ''}`, bad: true }
}

/** A meter reads as a problem from here up. */
export const FULL_AT = 90

export interface HealthView {
  memory: number | null
  disk: number | null
  /** "load 0.42 on 4 cores" */
  load: string | null
  /** "3 updates, 2 security", "up to date" */
  updates: string | null
  reboot: boolean
}

export function healthView(h: FleetHealth | null): HealthView | null {
  if (!h) return null
  const load = h.load === null ? null : `load ${h.load.toFixed(2)}${h.cores ? ` on ${h.cores} core${h.cores === 1 ? '' : 's'}` : ''}`
  const u = h.pendingUpdates
  const updates = !u ? null : u.total === 0 ? 'up to date' : `${u.total} update${u.total === 1 ? '' : 's'}${u.security ? `, ${u.security} security` : ''}`
  return { memory: h.memoryPercent, disk: h.diskPercent, load, updates, reboot: h.rebootRequired === true }
}

/** Needs a person: offline, failing, a light down, a full disk or memory. Behind on the agent is worth a look, not an alarm. */
export function needsAttention(row: FleetRow, onlineWithinSec: number, now: number): boolean {
  const ci = cloudInitWords(row.cloudInit)
  return (
    presence(row, onlineWithinSec, now) === 'offline' ||
    row.lights.some((l) => l.state === 'down') ||
    row.failingChecks.length > 0 ||
    Boolean(ci?.bad) ||
    (row.health?.diskPercent ?? 0) >= FULL_AT ||
    (row.health?.memoryPercent ?? 0) >= FULL_AT
  )
}

/** Problems first, then behind, then the rest; by founder name within each. */
export function sortFleet(rows: FleetRow[], onlineWithinSec: number, now: number): FleetRow[] {
  const rank = (r: FleetRow) => (needsAttention(r, onlineWithinSec, now) ? 0 : r.outdated ? 1 : 2)
  return [...rows].sort((a, b) => rank(a) - rank(b) || a.founder.name.localeCompare(b.founder.name))
}

/** The section head: "4 boxes · 3 online · 1 offline · 1 behind · 1 failing". Zero counts are left out. */
export function fleetSummary(fleet: Pick<Fleet, 'boxes' | 'onlineWithinSec'>, now: number): string {
  const n = fleet.boxes.length
  if (n === 0) return 'no boxes yet'
  const by = (p: Presence) => fleet.boxes.filter((r) => presence(r, fleet.onlineWithinSec, now) === p).length
  const parts = [
    `${n} box${n === 1 ? '' : 'es'}`,
    `${by('online')} online`,
    by('offline') ? `${by('offline')} offline` : '',
    by('unknown') ? `${by('unknown')} not reporting yet` : '',
    fleet.boxes.some((r) => r.outdated) ? `${fleet.boxes.filter((r) => r.outdated).length} behind` : '',
    fleet.boxes.some((r) => r.failingChecks.length) ? `${fleet.boxes.filter((r) => r.failingChecks.length).length} failing` : '',
  ]
  return parts.filter(Boolean).join(' · ')
}
